// 25+ Google Fonts for landing page customization
export const GOOGLE_FONTS = [
  // Sans-serif - Modern & Clean
  { name: 'Inter', value: 'Inter', category: 'Sans Serif', style: 'Modern' },
  { name: 'Poppins', value: 'Poppins', category: 'Sans Serif', style: 'Friendly' },
  { name: 'Roboto', value: 'Roboto', category: 'Sans Serif', style: 'Versatile' },
  { name: 'Open Sans', value: 'Open Sans', category: 'Sans Serif', style: 'Readable' },
  { name: 'Montserrat', value: 'Montserrat', category: 'Sans Serif', style: 'Bold' },
  { name: 'Lato', value: 'Lato', category: 'Sans Serif', style: 'Warm' },
  { name: 'Nunito', value: 'Nunito', category: 'Sans Serif', style: 'Rounded' },
  { name: 'Raleway', value: 'Raleway', category: 'Sans Serif', style: 'Elegant' },
  { name: 'Work Sans', value: 'Work Sans', category: 'Sans Serif', style: 'Geometric' },
  { name: 'DM Sans', value: 'DM Sans', category: 'Sans Serif', style: 'Geometric' },
  { name: 'Manrope', value: 'Manrope', category: 'Sans Serif', style: 'Modern' },
  { name: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans', category: 'Sans Serif', style: 'Contemporary' },
  { name: 'Space Grotesk', value: 'Space Grotesk', category: 'Sans Serif', style: 'Tech' },
  { name: 'Outfit', value: 'Outfit', category: 'Sans Serif', style: 'Fresh' },
  { name: 'Sora', value: 'Sora', category: 'Sans Serif', style: 'Futuristic' },
  { name: 'Quicksand', value: 'Quicksand', category: 'Sans Serif', style: 'Playful' },
  { name: 'Figtree', value: 'Figtree', category: 'Sans Serif', style: 'Clean' },
  { name: 'Lexend', value: 'Lexend', category: 'Sans Serif', style: 'Readable' },
  { name: 'Albert Sans', value: 'Albert Sans', category: 'Sans Serif', style: 'Modern' },

  // Serif - Classic & Elegant
  { name: 'Playfair Display', value: 'Playfair Display', category: 'Serif', style: 'Elegant' },
  { name: 'Merriweather', value: 'Merriweather', category: 'Serif', style: 'Readable' },
  { name: 'Lora', value: 'Lora', category: 'Serif', style: 'Calm' },
  { name: 'PT Serif', value: 'PT Serif', category: 'Serif', style: 'Classic' },
  { name: 'Libre Baskerville', value: 'Libre Baskerville', category: 'Serif', style: 'Traditional' },
  { name: 'Crimson Text', value: 'Crimson Text', category: 'Serif', style: 'Editorial' },
  { name: 'DM Serif Display', value: 'DM Serif Display', category: 'Serif', style: 'Display' },
  { name: 'Fraunces', value: 'Fraunces', category: 'Serif', style: 'Quirky' },
];

// Font size options (legacy preset system)
export const FONT_SIZES = [
  { value: 'small', label: 'Small', description: 'Compact text sizes' },
  { value: 'medium', label: 'Medium (Recommended)', description: 'Standard sizing' },
  { value: 'large', label: 'Large', description: 'Larger, more readable text' },
  { value: 'xlarge', label: 'Extra Large', description: 'Maximum impact' },
];

// ============================================================================
// ADVANCED TYPOGRAPHY SYSTEM
// ============================================================================

export interface TypographySettings {
  headingFont: string;
  bodyFont: string;
  headingWeight: number;
  bodyWeight: number;
  customSizes: boolean; // false = use preset, true = use custom px
  sizes: {
    desktop: { heading: number; subheading: number; body: number; small: number };
    tablet: { heading: number; subheading: number; body: number; small: number };
    mobile: { heading: number; subheading: number; body: number; small: number };
  };
}

export const FONT_WEIGHT_OPTIONS = [
  { value: 300, label: 'Light' },
  { value: 400, label: 'Regular' },
  { value: 500, label: 'Medium' },
  { value: 600, label: 'Semi Bold' },
  { value: 700, label: 'Bold' },
  { value: 800, label: 'Extra Bold' },
  { value: 900, label: 'Black' },
];

export const DEFAULT_TYPOGRAPHY: TypographySettings = {
  headingFont: 'Inter',
  bodyFont: 'Inter',
  headingWeight: 700,
  bodyWeight: 400,
  customSizes: false,
  sizes: {
    desktop: { heading: 60, subheading: 24, body: 18, small: 16 },
    tablet: { heading: 48, subheading: 24, body: 16, small: 14 },
    mobile: { heading: 36, subheading: 20, body: 16, small: 14 },
  },
};

/**
 * Parse saved typography settings from legacy or new format.
 * - Legacy: fontFamily = "Inter", fontSize = '{"mobile":"medium",...}'
 * - New:    fontFamily = '{"headingFont":"Playfair Display",...}'
 */
export function parseTypographySettings(
  fontFamily?: string,
  fontSize?: string
): TypographySettings {
  // Try new format first (fontFamily contains full JSON config)
  if (fontFamily && fontFamily.startsWith('{')) {
    try {
      const parsed = JSON.parse(fontFamily) as Partial<TypographySettings>;
      return {
        ...DEFAULT_TYPOGRAPHY,
        ...parsed,
        sizes: {
          desktop: { ...DEFAULT_TYPOGRAPHY.sizes.desktop, ...parsed.sizes?.desktop },
          tablet: { ...DEFAULT_TYPOGRAPHY.sizes.tablet, ...parsed.sizes?.tablet },
          mobile: { ...DEFAULT_TYPOGRAPHY.sizes.mobile, ...parsed.sizes?.mobile },
        },
      };
    } catch { /* fall through */ }
  }

  // Legacy: fontFamily is a plain font name
  const legacyFont = fontFamily || 'Inter';

  return {
    ...DEFAULT_TYPOGRAPHY,
    headingFont: legacyFont,
    bodyFont: legacyFont,
  };
}

/**
 * Serialize typography settings for saving.
 * Returns { fontFamily: string, fontSize: string } for backward-compat storage.
 */
export function serializeTypographySettings(
  typography: TypographySettings
): { fontFamily: string; fontSize: string } {
  return {
    fontFamily: JSON.stringify(typography),
    fontSize: JSON.stringify({ mobile: 'medium', tablet: 'medium', desktop: 'medium' }), // dummy for backward compat
  };
}

/**
 * Generate dynamic CSS classes for custom font sizes.
 * Returns { sizeClasses, cssText } — apply cssText via <style> tag.
 */
export function generateTypographyCSS(
  typography: TypographySettings,
  viewMode: 'desktop' | 'tablet' | 'mobile'
): { sizeClasses: { heading: string; subheading: string; body: string; small: string }; cssText: string } {
  const device = typography.sizes[viewMode];

  const sizeClasses = {
    heading: 'typo-heading',
    subheading: 'typo-subheading',
    body: 'typo-body',
    small: 'typo-small',
  };

  const cssText = `
    .typo-heading { font-size: ${device.heading}px !important; }
    .typo-subheading { font-size: ${device.subheading}px !important; }
    .typo-body { font-size: ${device.body}px !important; }
    .typo-small { font-size: ${device.small}px !important; }
  `;

  return { sizeClasses, cssText };
}

/**
 * Generate the full typography style tag for both fonts, weights, and sizes.
 */
export function generateFullTypographyCSS(
  typography: TypographySettings,
  viewMode: 'desktop' | 'tablet' | 'mobile'
): string {
  const device = typography.sizes[viewMode];

  return `
    .landing-page-font-override {
      font-family: '${typography.bodyFont}', sans-serif !important;
      font-weight: ${typography.bodyWeight} !important;
    }
    .landing-page-font-override h1,
    .landing-page-font-override h2,
    .landing-page-font-override h3,
    .landing-page-font-override h4 {
      font-family: '${typography.headingFont}', sans-serif !important;
      font-weight: ${typography.headingWeight} !important;
    }
    ${typography.customSizes ? `
    .typo-heading { font-size: ${device.heading}px !important; }
    .typo-subheading { font-size: ${device.subheading}px !important; }
    .typo-body { font-size: ${device.body}px !important; }
    .typo-small { font-size: ${device.small}px !important; }
    ` : ''}
  `;
}

export const getFontUrl = (fontFamily: string) => {
  return `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@300;400;500;600;700;800;900&display=swap`;
};

// Get all fonts URL for preloading in preview
export const getAllFontsUrl = (): string => {
  const fontFamilies = GOOGLE_FONTS.map(f => f.value.replace(/ /g, '+')).join('&family=');
  return `https://fonts.googleapis.com/css2?family=${fontFamilies}:wght@400;500;600;700&display=swap`;
};
